
import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const SecurityAssessments = () => {
  const tiers = [
    {
      name: 'Essential',
      price: '$2,999',
      description: 'Perfect for small businesses',
      features: [
        'Vulnerability Scanning',
        'Basic Risk Assessment',
        'Security Report',
        'Email Support',
        '1 Consultation'
      ],
      popular: false
    },
    {
      name: 'Professional',
      price: '$7,999',
      description: 'Ideal for growing companies',
      features: [
        'Comprehensive Penetration Testing',
        'Advanced Risk Analysis',
        'Detailed Security Report',
        'Priority Support',
        '3 Consultations',
        'Remediation Guidance'
      ],
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      description: 'For large organisations',
      features: [
        'Full Security Audit',
        'Advanced Threat Modeling',
        'Executive Summary Report',
        // '24/7 Support' removed — we do not run a 24/7 desk.
        'Priority Support',
        'Unlimited Consultations',
        'Ongoing Monitoring',
        'Compliance Assessment'
      ],
      popular: false
    }
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white w-full overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-[#422680] via-[#660F56] to-[#F54952] bg-clip-text text-transparent">
              Security Assessments
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Professional security evaluations to identify and address vulnerabilities
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {tiers.map((tier, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative bg-white rounded-2xl p-8 flex flex-col ${
                tier.popular 
                  ? 'border-4 border-[#F54952] shadow-2xl scale-105 z-10' 
                  : 'border-2 border-gray-100'
              } hover:shadow-xl transition-all`}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#F54952] text-white text-sm font-bold px-6 py-2 rounded-full">
                  Most Popular
                </div>
              )}
              
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                <p className="text-gray-600 mb-4">{tier.description}</p>
                <div className="text-4xl font-bold bg-gradient-to-r from-[#422680] to-[#F54952] bg-clip-text text-transparent">
                  {tier.price}
                </div>
              </div>

              <ul className="space-y-4 mb-8 flex-1">
                {tier.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="text-[#422680] shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link to="/assessments" className="mt-auto">
                <Button 
                  className={`w-full ${
                    tier.popular
                      ? 'bg-gradient-to-r from-[#422680] to-[#F54952] hover:opacity-90'
                      : 'bg-gray-900 hover:bg-gray-800'
                  } text-white`}
                >
                  Get Started
                </Button>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SecurityAssessments;
